import { useQuery } from "@tanstack/react-query";

import { $api, fetchClient, orNull } from "./client";
import type { components } from "./schema";

export type ForecastPoint = components["schemas"]["ForecastPoint"];
export type ExcludedEventDetail = components["schemas"]["ExcludedEventDetail"];
export type DemandHistoryGroup =
  components["schemas"]["DemandHistoryResponse"]["groups"][number];
export type DemandHistoryPoint = components["schemas"]["DemandHistoryPoint"];

/** Daily demand, optionally split by segment. `item` and `items` both narrow
 *  the items; every filter left out means all. */
export function useDemandHistory(p: {
  item?: number;
  items?: number[];
  labels?: number[];
  locations: number[];
  segments?: number[];
  mode: "raw" | "cleansed";
  splitBy?: "none" | "segment";
}) {
  return $api.useQuery("get", "/api/v1/demand/history/", {
    params: {
      query: {
        item: p.item !== undefined ? [p.item] : p.items,
        label: p.labels,
        location: p.locations,
        segment: p.segments,
        mode: p.mode,
        split_by: p.splitBy,
      },
    },
  });
}

/** Future-only forecast + cleansing byproducts; null without a forecast config. */
export function useForecast(item: number | undefined, location: number[]) {
  const init = { params: { query: { item: item ?? 0, location } } };
  return useQuery({
    queryKey: $api.queryOptions("get", "/api/v1/demand/forecast/", init)
      .queryKey,
    queryFn: ({ signal }) =>
      orNull(fetchClient.GET("/api/v1/demand/forecast/", { ...init, signal })),
    enabled: item !== undefined && location.length > 0,
  });
}

/** The events the current cleansing rule excludes. */
export const useDemandEvents = (
  item: number | undefined,
  location: number | undefined,
) =>
  $api.useQuery(
    "get",
    "/api/v1/demand/events/",
    { params: { query: { item: item ?? 0, location: location ?? 0 } } },
    { enabled: item !== undefined && location !== undefined },
  );
